import { formatAED, type AdminBillingSummary, type BillingEvent } from "@teamna/shared";
import type { BadgeTone } from "@teamna/ui/components/index.ts";
import { formatDate } from "@teamna/web-client/lib/format.ts";

/**
 * The words Control Center uses for Teamna billing (subscription billing spec, section 6.2): the
 * Billing column, its filter, and the lines of the billing feed. Short, for a table cell. Dates are
 * in the Teamna default timezone (Asia/Dubai), like the rest of Control Center.
 */

const DAY_MS = 86_400_000;

function daysUntil(value: string | null, now: Date): number | null {
  if (!value) return null;
  return Math.ceil((Date.parse(value) - now.getTime()) / DAY_MS);
}

function inDays(days: number): string {
  if (days <= 0) return "today";
  return days === 1 ? "in 1 day" : `in ${days} days`;
}

/** A business with no plan on purpose: a demo club, or a Teamna pilot (no trial, no charge). */
export type NoPlan = "demo" | "pilot" | null;

export function noPlanOf(business: { demoExpiresAt: string | null; pilotSince: string | null }): NoPlan {
  if (business.demoExpiresAt) return "demo";
  return business.pilotSince ? "pilot" : null;
}

/** The Billing cell: a badge and a short detail ("trial, 2 days", "past due, read-only in 1 day"). */
export function billingCell(
  billing: AdminBillingSummary | null,
  noPlan: NoPlan,
  now: Date,
): { tone: BadgeTone; label: string; detail: string | null } {
  if (noPlan === "demo") return { tone: "late", label: "Demo", detail: null };
  if (noPlan === "pilot") return { tone: "confirmed", label: "Pilot", detail: "no trial, no charge" };
  if (!billing || !billing.state) return { tone: "quiet", label: "No plan yet", detail: "billing-tick makes it" };
  if (billing.access === "read_only") {
    return { tone: "cancelled", label: "Read-only", detail: billing.state === "ended" ? "plan ended" : billing.state === "paused" ? "paused" : "not paid" };
  }
  switch (billing.state) {
    case "trialing": {
      const days = daysUntil(billing.trialEndsAt, now);
      return { tone: "quiet", label: "Trial", detail: days === null ? null : `${Math.max(days, 0)} day${days === 1 ? "" : "s"} left${billing.hasCard ? ", card on file" : ""}` };
    }
    case "active":
      return billing.monthlyFils > 0
        ? { tone: "confirmed", label: "Paying", detail: `${formatAED(billing.monthlyFils)} a month` }
        : { tone: "quiet", label: "Active", detail: "100% discount" };
    case "canceling": {
      const days = daysUntil(billing.currentPeriodEnd, now);
      return { tone: "late", label: "Cancelling", detail: days === null ? null : `ends ${inDays(days)}` };
    }
    case "past_due": {
      const days = daysUntil(billing.graceEndsAt, now);
      return { tone: "cancelled", label: "Past due", detail: days === null ? null : `read-only ${inDays(days)}` };
    }
    case "paused":
      return { tone: "quiet", label: "Paused", detail: billing.pausedUntil ? `until ${formatDate(billing.pausedUntil)}` : null };
    case "ended":
      return { tone: "quiet", label: "Ended", detail: null };
  }
}

export const BILLING_FILTERS = [
  { id: "all", label: "All billing" },
  { id: "trial", label: "Trial" },
  { id: "trial_soon", label: "Trial ends in 3 days or less" },
  { id: "past_due", label: "Past due" },
  { id: "read_only", label: "Read-only" },
  { id: "paying", label: "Paying" },
  { id: "canceling", label: "Cancelling" },
  { id: "ended", label: "Ended" },
  { id: "pilot", label: "Pilot" },
] as const;
export type BillingFilter = (typeof BILLING_FILTERS)[number]["id"];

export function matchesBillingFilter(
  business: { billing: AdminBillingSummary | null; pilotSince: string | null },
  filter: BillingFilter,
  now: Date,
): boolean {
  if (filter === "all") return true;
  if (filter === "pilot") return business.pilotSince !== null;
  const billing = business.billing;
  if (!billing) return false;
  switch (filter) {
    case "trial":
      return billing.state === "trialing";
    case "trial_soon": {
      const days = daysUntil(billing.trialEndsAt, now);
      return billing.state === "trialing" && days !== null && days <= 3;
    }
    case "past_due":
      return billing.state === "past_due";
    case "read_only":
      return billing.access === "read_only";
    case "paying":
      return (billing.state === "active" || billing.state === "canceling") && billing.monthlyFils > 0;
    case "canceling":
      return billing.state === "canceling";
    case "ended":
      return billing.state === "ended";
  }
}

const EVENT_NAMES: Record<BillingEvent["kind"], string> = {
  subscription_created: "Trial started",
  card_added: "Card added",
  payment_succeeded: "Payment",
  payment_failed: "Payment failed",
  state_changed: "State changed",
  read_only_started: "Read-only started",
  access_restored: "Full access again",
  reconcile_fixed: "Reconcile fixed",
  trial_ending: "Trial ends tomorrow, no card",
  email_changed: "Billing email changed",
  trial_changed: "Trial end changed",
  discount_applied: "Discount applied",
  discount_removed: "Discount removed",
  access_kept: "Access kept while unpaid",
  refunded: "Refund",
  paused: "Billing paused",
  resumed: "Billing resumed",
  credit_added: "Credit added",
  price_moved: "Moved to current price",
  package_added: "AI Assistant added",
  package_removed: "AI Assistant removed",
  pilot_started: "Pilot started",
  pilot_ended: "Pilot ended",
};

export function eventName(kind: BillingEvent["kind"]): string {
  return EVENT_NAMES[kind] ?? kind;
}

/** The detail of one feed line, from the event's data. */
export function eventDetail(event: BillingEvent): string {
  const data = event.data;
  const money = (key: string) => (typeof data[key] === "number" ? formatAED(data[key] as number) : null);
  switch (event.kind) {
    case "payment_succeeded":
    case "refunded":
    case "credit_added":
      return money("amountFils") ?? "";
    case "state_changed": {
      const why = [data.feedback, data.comment].filter((value) => typeof value === "string" && value !== "").join(". ");
      return `${String(data.from ?? "?")} to ${String(data.to ?? "?")}${why ? ` (${why})` : ""}`;
    }
    case "discount_applied": {
      const off = typeof data.percentOff === "number" ? `${data.percentOff}% off` : `${money("amountOffFils") ?? "?"} off`;
      return data.duration === "repeating" ? `${off} for ${String(data.months)} months` : data.duration === "forever" ? `${off}, no end date` : `${off}, one invoice`;
    }
    case "trial_changed":
      return data.to === "now" ? "ended now" : typeof data.to === "string" ? `to ${formatDate(data.to)}` : "";
    case "access_kept":
      return data.days === 0 ? "ended" : `${String(data.days)} days`;
    case "paused":
      return `${data.access === "read_only" ? "Desk read-only" : "Desk full"}`;
    case "reconcile_fixed":
      return Array.isArray(data.fields) ? data.fields.join(", ") : "";
    case "package_added":
      return data.kept === true ? "kept after a Remove" : "";
    case "package_removed":
      return typeof data.endsAt === "string" ? `on until ${formatDate(data.endsAt)}` : "";
    case "pilot_started":
      return typeof data.cancelledSubscriptionId === "string" ? "trial cancelled" : "";
    case "pilot_ended":
      return typeof data.trialEndsAt === "string" ? `trial until ${formatDate(data.trialEndsAt)}` : "trial starts when billing is on";
    default:
      return "";
  }
}
